﻿<template>
    <div class="flex flex-column w-full" v-if="ready">
        <TopBar v-if="!route.meta.hideTopBar" class="flex"></TopBar>
        <div class="flex">
            <RouterView :key="refreshKey" />
        </div>
    </div>
    <Toaster></Toaster>
    <Messenger></Messenger>
    <!--zIndex.modal:1100, 避免Dialog使用confirm時被遮-->
    <ConfirmDialog group="dialog">
        <template #message="slotProps">
            <div class="flex">
                <i :class="slotProps.message.icon" style="font-size: 1.5rem"></i>
                <div class="pl-2" v-html="slotProps.message.message"></div>
            </div>
        </template>
    </ConfirmDialog>
    <ConfirmPopup group="popup">
    </ConfirmPopup>
    <div style="position: fixed; width: 100%; height: 100%; align-content: center; top: 0vh; display:block;margin:1px,auto;" v-if="appBusy||lazyBusy">
        <ProgressSpinner style="width:100px;height:100px; display:block;" strokeWidth="8" fill="#EEEEEE" animationDuration=".5s" />
        <h2 class="flex justify-content-center text-yellow-200 bg-blue-700" style="line-height:4rem;" v-if="!!busyWords">{{busyWords}}</h2>
    </div>
    <!-- 佔用視窗 -->
    <div class="p-blockui-mask p-overlay-mask p-overlay-mask-enter p-blockui-mask-document" v-if="lazyBusy||appBusy"></div>
</template>

<script lang="ts">
    export default {
        name: 'App',
        inheritAttrs: false
    }
</script>

<script setup lang="ts">
    import { onMounted, onUnmounted, ref, watch } from 'vue'
    import { utilfcty$ } from './utils/utility'
    import { storeToRefs } from 'pinia'
    import { module$ } from './enums'
    import { useI18n } from 'vue-i18n';
    import { setDocumentTitle } from './locales';
    import { useRoute } from 'vue-router'
    import { usePrimeVue } from 'primevue/config';

    // ***************工具物件***************
    //util$物件
    const util$ = utilfcty$.vueUse();
    const { locale, getLocaleMessage } = useI18n();
    const route = useRoute()
    const primevue = usePrimeVue();

    // ***************元件屬性|計算**********
    const { ready, appBusy, busyWords, refreshKey } = storeToRefs(util$.pinia$.app);
    //載入頁面時顯示ProgressSpinner
    const lazyBusy = ref(false);

    // ***************函式/事件**************
    const handleApiError = (event: CustomEvent) => {
        const { summary, detail, traceId, status } = event.detail;

        util$.toast$?.add({
            severity: status >= 500 ? 'error' : 'warn',
            summary: summary,
            detail: `${detail} (Trace ID: ${traceId})`,
            life: 5000 // 顯示 5 秒
        });
    };

    // axios 錯誤都已由 http.ts 攔截器（handleAxiosError）集中處理過（toast/導向/log）。
    // 對於沒有 catch 的呼叫端，其 reject 會冒泡為 unhandledrejection；因錯誤已處理，
    // 此處靜默掉，避免 console 出現 Uncaught 雜訊。有 catch 的呼叫端不受影響
    // （unhandledrejection 僅在「無人 catch」時觸發）。
    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
        if (event.reason?.isAxiosError) {
            event.preventDefault();
        }
    };

    // ***************元件監看***************
    util$.vueRouter$.beforeEach(async (to, from) => {
        //動態載入vue時顯示spinner
        lazyBusy.value = true;
        // 匿名可訪問的頁（路由標 meta.allowAnonymous）→ 不做登入攔截。
        // 之後新增公開頁，只在該路由標 meta 即可，此處無需改動。
        if (to.meta.allowAnonymous === true) return true
        if (!util$.pinia$.security.isAuthenticated() && to.name !== module$.login) {
            // 帶上登入前嘗試訪問的完整路徑（to.fullPath），登入成功後導回該頁，而非一律回 home。
            return { name: module$.login, query: { redirect: to.fullPath } }
        }
        if (util$.pinia$.security.isAuthenticated() && (to.name == module$.login || to.path == '/')) {
            // 已登入卻進 login／"/"：若帶站內 redirect（如公布頁、指定落地頁連結），直接導向該頁；
            // 否則回登入後首頁。redirect 僅接受站內路徑（/開頭、排除 //），外部網址（http(s)://、//）一律忽略改回 home，防站外導向。
            const redirect = to.query.redirect as string | undefined;
            if (redirect && /^\/(?!\/)/.test(redirect)) {
                return redirect;
            }
            return util$.router$.resolveHome();
        }
    })
    util$.vueRouter$.afterEach((to, from) => {
        //即時載入vue完成關閉spinner
        lazyBusy.value = false;
    })

    // 監聽語系切換:同時更新「文件標題」與「PrimeVue 語系配置」
    watch(locale, (newLocale) => {
        // (0) 持久化語系選擇:不論從何處改 locale 都記住,F5 後由 i18n 初始化讀回
        localStorage.setItem('locale', newLocale as string);

        // (1) 更新文件標題(同頁切換語系時;組字邏輯統一於 locales 的 setDocumentTitle,與 router 導航共用)
        setDocumentTitle(route.meta.titleKey as string | undefined)

        // (2) 更新 PrimeVue 全域語系配置
        const messages = getLocaleMessage(newLocale) as any;
        if (messages && messages.primevue) {
            primevue.config.locale = messages.primevue.primevue;
        }
    }, { immediate: true });

    // ***************元件鉤子***************
    onMounted(async () => {
        window.addEventListener('api-error', handleApiError as EventListener);
        window.addEventListener('unhandledrejection', handleUnhandledRejection); 
        await util$.pinia$.app.initialize();

        // 套用後端 ClientLog.Level（僅在無 localStorage 覆寫時；覆寫優先）。
        // 缺值（舊版後端）→ 不呼叫；非法值由 setLevel 內部忽略並保留現值。
        const backendLogLevel = util$.pinia$.app.parameters?.clientLog?.level as string | undefined;
        if (!localStorage.getItem('logLevel') && backendLogLevel) {
            util$.log$.setLevel(backendLogLevel);
        }

        //reset to false
        lazyBusy.value = false;
    });

    onUnmounted(() => {
        window.removeEventListener('api-error', handleApiError as EventListener);
        window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    });

</script>


<!--DataTable-->
<style scoped>
</style>

<!--Toast-->
<style scoped>
</style>